import type { Environment } from '@fleequid-secrets/shared';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { api, messageOf } from '@/api/client';
import { Icon } from '@/components/Icon';
import { SectionBoard } from '@/components/SectionBoard';
import { NewSection, SectionRail } from '@/components/SectionRail';
import { Link, navigate, useSearch } from '@/lib/router';
import { vaultPath } from '@/lib/routes';
import { useShell } from '@/lib/shell';
import { Placeholder } from './Placeholder';

// VARIABLES: /<environment>/<section>. Without a section, the environment's first; without environments, the way to
// make them.
export function Vault(props: { env: string; section: string | null }): ReactNode {
    const { environments } = useShell();
    const search = useSearch();
    const highlight = new URLSearchParams(search).get('key');
    const environment = environments.find((item) => item.slug === props.env);
    const section = environment?.sections.find((item) => item.slug === props.section) ?? (props.section ? undefined : environment?.sections[0]);

    useEffect(() => {
        if (environment && !props.section && section) {
            navigate(vaultPath(environment.slug, section.slug), { replace: true });
        }
    }, [environment, props.section, section]);

    if (!environment) {
        return environments.length === 0 ? (
            <FirstEnvironments />
        ) : (
            <Placeholder title="No such environment" text={`Nothing lives at /${props.env}.`} action={{ label: 'Back to the variables', to: '/' }} />
        );
    }

    return (
        <main className="vault page">
            <SectionRail environment={environment} activeSlug={section?.slug ?? null} />
            {section ? (
                <SectionBoard key={section.id} environment={environment} sectionId={section.id} highlight={highlight} />
            ) : props.section ? (
                <section className="board panel">
                    <div className="empty">
                        <b>
                            {environment.name} has no section at /{props.section}
                        </b>
                        <Link className="link" to={vaultPath(environment.slug)}>
                            Open {environment.name}
                        </Link>
                    </div>
                </section>
            ) : (
                <NoSections environment={environment} />
            )}
        </main>
    );
}

function NoSections(props: { environment: Environment }): ReactNode {
    return (
        <section className="board panel">
            <div className="empty" style={{ maxWidth: 520, margin: '40px auto 0' }}>
                <b>{props.environment.name} has no sections yet</b>A section holds the variables of one part of the platform: Backend, Frontend, Cron.
                Name the first one.
            </div>
            <div style={{ width: 420, maxWidth: 'calc(100% - 32px)', margin: '0 auto' }}>
                <NewSection environment={props.environment} />
            </div>
        </section>
    );
}

// A new vault: the usual environments in one click (an admin's), or the page to make one's own.
function FirstEnvironments(): ReactNode {
    const { user, say } = useShell();
    const queryClient = useQueryClient();
    const [busy, setBusy] = useState(false);

    return (
        <main className="page page-scroll">
            <div className="placeholder" style={{ maxWidth: 560 }}>
                <Icon name="layers" size={28} />
                <h2>No environments yet</h2>
                {user.role === 'admin' ? (
                    <>
                        <p>
                            Start with the usual five: Production (protected: every change asks first), Preproduction, Demo, Staging and Development.
                            Each gets its own encryption key.
                        </p>
                        <div className="empty-actions">
                            <button
                                className="act primary"
                                disabled={busy}
                                onClick={() => {
                                    setBusy(true);
                                    api.createDefaults()
                                        .then(() => queryClient.invalidateQueries({ queryKey: ['environments'] }))
                                        .catch((error: unknown) => say(messageOf(error)))
                                        .finally(() => setBusy(false));
                                }}
                            >
                                <Icon name="plus" size={15} />
                                {busy ? 'Creating…' : 'Create the usual environments'}
                            </button>
                            <Link className="act" to="/environments">
                                Make my own
                            </Link>
                        </div>
                    </>
                ) : (
                    <p>An admin creates the environments; then their sections and variables show up here.</p>
                )}
            </div>
        </main>
    );
}
